import { cloneDeep, isEqual } from 'lodash-es'
import { hasMedicationSignature } from 'src/utils/studentMedications'
import type {
  IImmunizationDose,
  IImmunizationInfo,
  IImmunizationRecord,
  IMedication,
  IMember,
  IStudent,
} from 'src/interfaces/family'
import { authInfoTemplate } from 'src/templates/family'

const VALID_STUDENT_PROPERTIES = new Set<keyof IStudent>([
  'livesWithMemberIds',
  'prohibitedFoods',
  'healthCareNumber',
  'phinNumber',
  'medicalPractitionerId',
  'medicalPractitioner',
  'immunizationInfo',
  'medicalConditions',
  'medications',
  'specialNeeds',
  'dateOfBirth',
  'careType',
  'preferredName',
  'firstName',
  'middleName',
  'lastName',
  'gender',
])

const VALID_MEMBER_PROPERTIES = new Set<keyof IMember>([
  'authorizationInfo',
  'personalContactInfo',
  'workContactInfo',
  'relationship',
  'isAuthorizedSponsor',
  'occupation',
  'preferredName',
  'firstName',
  'middleName',
  'lastName',
])

const VALID_SIMPLE_MEMBER_PROPERTIES = new Set<keyof IMember>(
  [...VALID_MEMBER_PROPERTIES].filter(
    (prop) => !['authorizationInfo'].includes(prop),
  ),
)

const VALID_SIMPLE_STUDENT_PROPERTIES = new Set<keyof IStudent>(
  [...VALID_STUDENT_PROPERTIES].filter(
    (prop) => prop !== 'immunizationInfo' && prop !== 'medications',
  ),
)

const hasText = (value?: string | null) =>
  typeof value === 'string' && value.trim().length > 0

function normalizeImmunizationRecord(
  record: IImmunizationRecord,
  originalRecords: IImmunizationRecord[] = [],
): IImmunizationRecord | undefined {
  const originalRecord = originalRecords.find((candidate) =>
    matchesImmunizationRecord(candidate, record),
  )
  const doses = record.doses
    ?.filter((dose) => hasText(dose.dateGiven))
    .map((dose) => {
      const originalDose = originalRecord?.doses?.find((candidate) =>
        matchesImmunizationDose(candidate, dose),
      )
      return {
        doseId: originalDose?.doseId ?? dose.doseId,
        name: originalDose?.name ?? dose.name ?? '',
        dueAgeInMonths: dose.dueAgeInMonths,
        dueDate: dose.dueDate,
        dateGiven: dose.dateGiven,
      }
    })
  const normalizedRecord = {
    vaccineId: originalRecord?.vaccineId ?? record.vaccineId,
    name: originalRecord?.name ?? record.name ?? '',
    exemptionReason: record.exemptionReason ?? '',
    lastDoseDate: record.lastDoseDate ?? '',
    nextDueDate: record.nextDueDate ?? '',
    ...(doses?.length ? { doses } : {}),
  }

  const hasMeaningfulFields =
    hasText(normalizedRecord.exemptionReason) ||
    hasText(normalizedRecord.lastDoseDate) ||
    hasText(normalizedRecord.nextDueDate) ||
    Boolean(normalizedRecord.doses?.length)

  const hadMeaningfulFields = originalRecord
    ? hasText(originalRecord.exemptionReason) ||
      hasText(originalRecord.lastDoseDate) ||
      hasText(originalRecord.nextDueDate) ||
      Boolean(originalRecord.doses?.some((dose) => hasText(dose.dateGiven)))
    : false

  return hasMeaningfulFields || hadMeaningfulFields
    ? normalizedRecord
    : undefined
}

export function normalizeImmunizationInfoForComparison(
  immunizationInfo: IImmunizationInfo | undefined,
  originalInfo?: IImmunizationInfo,
) {
  const normalizedImmunizationInfo = cloneDeep(
    immunizationInfo ?? {
      isImmunizationsUpToDate: false,
      immunizationRecord: [],
    },
  )

  const normalizedRecords =
    normalizedImmunizationInfo.immunizationRecord
      ?.map((record) =>
        normalizeImmunizationRecord(record, originalInfo?.immunizationRecord),
      )
      .filter(
        (record): record is IImmunizationRecord => record !== undefined,
      ) ?? []

  return {
    isImmunizationsUpToDate:
      normalizedImmunizationInfo.isImmunizationsUpToDate ?? false,
    immunizationRecord: normalizedRecords,
    immunizationRecordFileUrl: [],
  }
}

export function areImmunizationInfosEqual(
  left: IImmunizationInfo | undefined,
  right: IImmunizationInfo | undefined,
) {
  return (
    JSON.stringify(canonicalizeImmunizationInfo(left)) ===
    JSON.stringify(canonicalizeImmunizationInfo(right))
  )
}

function canonicalizeImmunizationInfo(
  immunizationInfo: IImmunizationInfo | undefined,
) {
  const normalized = normalizeImmunizationInfoForComparison(immunizationInfo)
  const records = normalized.immunizationRecord.map((record) => ({
    exemptionReason: record.exemptionReason,
    lastDoseDate: record.lastDoseDate,
    nextDueDate: record.nextDueDate,
    doses: record.doses
      ?.map((dose) => ({
        dueAgeInMonths: dose.dueAgeInMonths,
        dueDate: dose.dueDate,
        dateGiven: dose.dateGiven,
      }))
      .sort((left, right) =>
        JSON.stringify(left).localeCompare(JSON.stringify(right)),
      ),
  }))

  return {
    isImmunizationsUpToDate: normalized.isImmunizationsUpToDate,
    immunizationRecord: records.sort((left, right) =>
      JSON.stringify(left).localeCompare(JSON.stringify(right)),
    ),
  }
}

function normalizeMedicationForComparison(medication: IMedication) {
  return {
    id: medication.id,
    name: medication.name,
    purpose: medication.purpose,
    dosage: medication.dosage,
    type: medication.type,
    hasMedicationSignature: hasMedicationSignature(medication),
    administrationMethod: medication.administrationMethod,
    administrationMethodOther:
      medication.administrationMethod === 'Other'
        ? medication.administrationMethodOther
        : undefined,
    isInOriginalContainer: medication.isInOriginalContainer,
    isChildNameOnLabel: medication.isChildNameOnLabel,
    isPrescribedByPhysician: medication.isPrescribedByPhysician,
    expiryDate: medication.expiryDate,
    administrationInfo: medication.administrationInfo
      ? {
          startDate: medication.administrationInfo.startDate,
          endDate: medication.administrationInfo.endDate,
          timesToBeAdministered:
            medication.administrationInfo.timesToBeAdministered,
          instructions: medication.administrationInfo.instructions,
        }
      : undefined,
  }
}

export function normalizeMedicationsForComparison(
  medications: IMedication[] | undefined,
) {
  return medications?.map(normalizeMedicationForComparison) ?? []
}

// The shared editor also contains admin-only fields that must not be added to
// the client info-change payload whitelist. Compare the full editor entity,
// substituting semantic comparisons only for attachment/setup-heavy sections.
export function areStudentEditorValuesEqual(
  originalStudent: IStudent | undefined,
  updatedStudent: IStudent | undefined,
  options: { includeTrackedImmunizationFields?: boolean } = {},
) {
  if (!originalStudent || !updatedStudent) {
    return originalStudent === updatedStudent
  }

  const {
    immunizationInfo: originalImmunizationInfo,
    medications: originalMedications,
    ...originalFields
  } = originalStudent
  const {
    immunizationInfo: updatedImmunizationInfo,
    medications: updatedMedications,
    ...updatedFields
  } = updatedStudent

  const immunizationValuesAreEqual =
    options.includeTrackedImmunizationFields === false
      ? isEqual(
          originalImmunizationInfo?.isImmunizationsUpToDate ?? false,
          updatedImmunizationInfo?.isImmunizationsUpToDate ?? false,
        )
      : areImmunizationInfosEqual(
          originalImmunizationInfo,
          updatedImmunizationInfo,
        )

  return (
    isEqual(originalFields, updatedFields) &&
    immunizationValuesAreEqual &&
    isEqual(
      normalizeMedicationsForComparison(originalMedications),
      normalizeMedicationsForComparison(updatedMedications),
    )
  )
}

export function applyChangesForMember(
  member: IMember,
  changes: Partial<IMember>,
): IMember {
  const updatedMember = cloneDeep(member)

  for (const key of Object.keys(changes) as Array<keyof IMember>) {
    if (VALID_SIMPLE_MEMBER_PROPERTIES.has(key)) {
      updatedMember[key] = changes[key] as never
    }
  }

  if (changes.authorizationInfo) {
    if (!updatedMember.authorizationInfo) {
      updatedMember.authorizationInfo = cloneDeep(authInfoTemplate)
    }

    if (changes.authorizationInfo.memberType) {
      updatedMember.authorizationInfo.memberType =
        changes.authorizationInfo.memberType
    }
    if (changes.authorizationInfo.pickupAuthorization) {
      updatedMember.authorizationInfo.pickupAuthorization =
        changes.authorizationInfo.pickupAuthorization
    }
  }

  return updatedMember
}

export function applyChangesForStudent(
  student: IStudent,
  changes: Partial<IStudent>,
): IStudent {
  const updatedStudent = cloneDeep(student)

  for (const key of Object.keys(changes) as Array<keyof IStudent>) {
    if (VALID_SIMPLE_STUDENT_PROPERTIES.has(key)) {
      updatedStudent[key] = changes[key] as never
    }
  }

  if (changes.immunizationInfo) {
    if (!updatedStudent.immunizationInfo) {
      updatedStudent.immunizationInfo = {
        immunizationRecord: [],
        isImmunizationsUpToDate: false,
        immunizationRecordFileUrl: [],
      }
    }

    if (
      changes.immunizationInfo.isImmunizationsUpToDate !== undefined &&
      changes.immunizationInfo.isImmunizationsUpToDate !== null
    ) {
      updatedStudent.immunizationInfo.isImmunizationsUpToDate =
        changes.immunizationInfo.isImmunizationsUpToDate
    }
    if (changes.immunizationInfo.immunizationRecord) {
      updatedStudent.immunizationInfo.immunizationRecord =
        mergeImmunizationRecordsWithStoredAttachments(
          updatedStudent.immunizationInfo.immunizationRecord,
          changes.immunizationInfo.immunizationRecord,
        )
    }
  }

  if (changes.medications) {
    updatedStudent.medications = changes.medications
  }

  return updatedStudent
}

function mergeImmunizationRecordsWithStoredAttachments(
  storedRecords: IImmunizationRecord[] | undefined,
  changedRecords: IImmunizationRecord[],
) {
  const stored = storedRecords ?? []
  const mergedRecords = changedRecords.map((changedRecord) => {
    const storedRecord = stored.find((record) =>
      matchesImmunizationRecord(record, changedRecord),
    )
    if (!storedRecord) return changedRecord

    const mergedRecord = {
      ...storedRecord,
      ...changedRecord,
      vaccineId: changedRecord.vaccineId ?? storedRecord.vaccineId,
      name: hasText(changedRecord.name)
        ? changedRecord.name
        : storedRecord.name,
      recordFileUrls: storedRecord.recordFileUrls ?? [],
      exemptionFileUrls: storedRecord.exemptionFileUrls ?? [],
      doses: mergeImmunizationDosesWithStoredAttachments(
        storedRecord.doses,
        changedRecord.doses,
      ),
    }

    if (
      hasText(storedRecord.exemptionReason) &&
      !hasText(changedRecord.exemptionReason)
    ) {
      mergedRecord.exemptionReason = ''
    }

    return mergedRecord
  })

  const orderedRecords = stored.flatMap((storedRecord) => {
    const changedIndex = mergedRecords.findIndex((changedRecord) =>
      matchesImmunizationRecord(storedRecord, changedRecord),
    )
    if (changedIndex >= 0) return mergedRecords.splice(changedIndex, 1)

    return normalizeImmunizationRecord(storedRecord) ? [] : [storedRecord]
  })

  return [...orderedRecords, ...mergedRecords]
}

function mergeImmunizationDosesWithStoredAttachments(
  storedDoses: IImmunizationDose[] | undefined,
  changedDoses: IImmunizationDose[] | undefined,
) {
  const stored = storedDoses ?? []
  const changed = changedDoses ?? []
  const mergedDoses = changed.map((changedDose) => {
    const storedDose = stored.find((dose) =>
      matchesImmunizationDose(dose, changedDose),
    )
    return storedDose
      ? {
          ...storedDose,
          ...changedDose,
          doseId: changedDose.doseId ?? storedDose.doseId,
          name: hasText(changedDose.name) ? changedDose.name : storedDose.name,
          fileUrls: storedDose.fileUrls ?? [],
        }
      : changedDose
  })

  return [
    ...mergedDoses,
    ...stored.filter(
      (storedDose) =>
        !changed.some((changedDose) =>
          matchesImmunizationDose(storedDose, changedDose),
        ),
    ),
  ]
}

function matchesImmunizationRecord(
  left: IImmunizationRecord,
  right: IImmunizationRecord,
) {
  if (left.vaccineId && right.vaccineId) {
    return left.vaccineId === right.vaccineId
  }
  const leftName = left.name?.trim().toLowerCase()
  const rightName = right.name?.trim().toLowerCase()
  return Boolean(leftName && rightName && leftName === rightName)
}

function matchesImmunizationDose(
  left: IImmunizationDose,
  right: IImmunizationDose,
) {
  if (left.doseId && right.doseId) {
    return left.doseId === right.doseId
  }
  const leftName = left.name?.trim().toLowerCase()
  const rightName = right.name?.trim().toLowerCase()
  return Boolean(leftName && rightName && leftName === rightName)
}

export function getChangedPropertiesForMember(
  originalMember: IMember,
  updatedMember: IMember,
): Partial<IMember> {
  const changes: Partial<IMember> = {}

  if (!originalMember) {
    return markAllValidMemberPropertiesAsChanged(changes, updatedMember)
  }

  for (const key of VALID_SIMPLE_MEMBER_PROPERTIES) {
    if (
      JSON.stringify(originalMember[key]) !== JSON.stringify(updatedMember[key])
    ) {
      changes[key] = updatedMember[key] as any
    }
  }

  if (updatedMember.authorizationInfo && originalMember.authorizationInfo) {
    changes.authorizationInfo = {}

    if (
      updatedMember.authorizationInfo.pickupAuthorization !=
      originalMember.authorizationInfo.pickupAuthorization
    ) {
      changes.authorizationInfo.pickupAuthorization =
        updatedMember.authorizationInfo.pickupAuthorization
    }

    if (
      updatedMember.authorizationInfo.memberType !=
      originalMember.authorizationInfo.memberType
    ) {
      changes.authorizationInfo.memberType =
        updatedMember.authorizationInfo.memberType
    }
  }

  return changes
}

export function getChangedPropertiesForStudent(
  originalStudent: IStudent,
  updatedStudent: IStudent,
  options: { includeTrackedImmunizationFields?: boolean } = {},
): Partial<IStudent> {
  const changes: Partial<IStudent> = {}

  if (!originalStudent) {
    const allChanges = markAllValidStudentPropertiesAsChanged(
      changes,
      updatedStudent,
    )
    return options.includeTrackedImmunizationFields === false
      ? omitTrackedImmunizationFields(allChanges)
      : allChanges
  }

  for (const key of VALID_SIMPLE_STUDENT_PROPERTIES) {
    if (
      JSON.stringify(originalStudent[key]) !==
      JSON.stringify(updatedStudent[key])
    ) {
      changes[key] = updatedStudent[key] as any
    }
  }

  const updatedImmunizationInfo = normalizeImmunizationInfoForComparison(
    updatedStudent.immunizationInfo,
    originalStudent.immunizationInfo,
  )
  const originalImmunizationInfo = normalizeImmunizationInfoForComparison(
    originalStudent.immunizationInfo,
  )

  if (
    !areImmunizationInfosEqual(
      originalStudent.immunizationInfo,
      updatedStudent.immunizationInfo,
    )
  ) {
    changes['immunizationInfo'] = orderChangedImmunizationInfo(
      updatedImmunizationInfo,
      originalImmunizationInfo,
    )
  }

  const originalMedications = normalizeMedicationsForComparison(
    originalStudent.medications,
  )
  const updatedMedications = normalizeMedicationsForComparison(
    updatedStudent.medications,
  )

  if (
    JSON.stringify(originalMedications) !== JSON.stringify(updatedMedications)
  ) {
    changes.medications = cloneDeep(updatedStudent.medications ?? [])
  }

  return options.includeTrackedImmunizationFields === false
    ? omitTrackedImmunizationFields(changes)
    : changes
}

function omitTrackedImmunizationFields(
  changes: Partial<IStudent>,
): Partial<IStudent> {
  if (!changes.immunizationInfo) return changes

  changes.immunizationInfo = {
    isImmunizationsUpToDate: changes.immunizationInfo.isImmunizationsUpToDate,
  } as IImmunizationInfo
  return changes
}

function orderChangedImmunizationInfo(
  changedInfo: IImmunizationInfo,
  originalInfo: IImmunizationInfo,
) {
  const records = orderByExistingItems(
    changedInfo.immunizationRecord,
    originalInfo.immunizationRecord,
    matchesImmunizationRecord,
  )

  return {
    ...changedInfo,
    immunizationRecord: records.map((record) => {
      const originalRecord = originalInfo.immunizationRecord.find((candidate) =>
        matchesImmunizationRecord(candidate, record),
      )
      if (!record.doses || !originalRecord?.doses) return record

      return {
        ...record,
        doses: orderByExistingItems(
          record.doses,
          originalRecord.doses,
          matchesImmunizationDose,
        ),
      }
    }),
  }
}

function orderByExistingItems<T>(
  changedItems: T[],
  originalItems: T[],
  matches: (left: T, right: T) => boolean,
) {
  const remaining = [...changedItems]
  const ordered = originalItems.flatMap((originalItem) => {
    const changedIndex = remaining.findIndex((changedItem) =>
      matches(originalItem, changedItem),
    )
    if (changedIndex < 0) return []
    return remaining.splice(changedIndex, 1)
  })
  return [...ordered, ...remaining]
}

function markAllValidMemberPropertiesAsChanged(
  changes: Partial<IMember>,
  updatedMember: IMember,
) {
  for (const key of VALID_MEMBER_PROPERTIES) {
    changes[key] = updatedMember[key] as any
  }
  if (updatedMember.authorizationInfo) {
    changes['authorizationInfo'] = {
      memberType: updatedMember.authorizationInfo.memberType,
      pickupAuthorization: updatedMember.authorizationInfo.pickupAuthorization,
    }
  }

  return changes
}

function markAllValidStudentPropertiesAsChanged(
  changes: Partial<IStudent>,
  updatedStudent: IStudent,
) {
  for (const key of VALID_SIMPLE_STUDENT_PROPERTIES) {
    changes[key] = updatedStudent[key] as any
  }

  changes['immunizationInfo'] = normalizeImmunizationInfoForComparison(
    updatedStudent.immunizationInfo,
  )
  changes.medications = cloneDeep(updatedStudent.medications ?? [])

  return changes
}
